/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ExportadorFabricante } from 'quickcomex-api-types';
import i18n from '../../i18n';
import {
  BACKGROUND_CREATE_MODE,
  CONSULT_MODE,
  CREATE_MODE,
  NONE_BACKGROUND_MODE,
  NONE_MODE,
  NONE_SUB_MODE,
  TRANSFER_MODE,
  UPDATE_MODE,
} from '../mode';
import {
  ALTERNATIVE_LOADING_STATUS,
  FAILURE_STATUS,
  IDLE_STATUS,
  LOADING_STATUS,
  PREPARING_ACTION_STATUS,
  SUCCESS_STATUS,
} from '../status';
import {
  activateById,
  fetchAll,
  fetchById,
  fetchFilteredByName,
  fetchPaginate,
  inactivateById,
} from './empresaEstrangeiraAPI';

interface EmpresaEstrangeiraInitialState {
  status: string;
  mode: {
    main: string;
    background: string;
    sub: string;
  };
  error: any;
  response: {
    status?: number | null;
    message?: string | null;
    data: any;
  };
  list: any[];
  model: any;
  related: {
    list: {
      selectionOperadores: any[];
    };
  };
  count?: number;
}

const initialState: EmpresaEstrangeiraInitialState = {
  status: IDLE_STATUS,
  mode: {
    main: NONE_MODE,
    background: NONE_BACKGROUND_MODE,
    sub: NONE_SUB_MODE,
  },
  error: null,
  response: {
    status: null,
    message: null,
    data: null,
  },
  list: [],
  model: {},
  related: {
    list: {
      selectionOperadores: [],
    },
  },
  count: 0,
};

const fetchPaginateAsync = createAsyncThunk(
  'empresaEstrangeira/fetchPaginate',
  async (params: [], { rejectWithValue }) => {
    const { data } = await fetchPaginate(params);

    return {
      data: {
        itens: data.itens,
        totalNumberOfItens: data.totalNumberOfItens,
      },
    };
  }
);

const fetchAllAsync = createAsyncThunk('empresaEstrangeira/fetchAll', async () => {
  const { data } = await fetchAll();
  return { data };
});

const fetchFilteredAsync = createAsyncThunk('empresaEstrangeira/fetchFiltered', async (name) => {
  const { data } = await fetchFilteredByName({ name: 'name', value: name });
  return { data };
});

const fetchByIdAndInsertList = createAsyncThunk('empresaEstrangeira/fetchByIdAndInsert', async (data) => {
  const response = await fetchById(data);
  return {
    response: {
      status: response.status,
      data: response.data,
    },
  };
});

const fetchByIdAsync = createAsyncThunk('empresaEstrangeira/fetchById', async (data) => {
  const response = await fetchById(data);
  return {
    response: {
      status: response.status,
      data: response.data,
    },
  };
});

const activateByIdAsync = createAsyncThunk('empresaEstrangeira/activateById', async (id) => {
  const { status, data } = await activateById(id);
  return { response: { status, data } };
});

const inactivateByIdAsync = createAsyncThunk('empresaEstrangeira/inactivateById', async (id) => {
  const { status, data } = await inactivateById(id);
  return { response: { status, data } };
});

const empresaEstrangeiraSlice = createSlice({
  name: 'empresaEstrangeira',
  initialState,
  reducers: {
    changeStatusTo: (state, action) => {
      state.status = action.payload.status;
    },
    loading: (state) => {
      state.status = LOADING_STATUS;
    },
    success: (state) => {
      state.status = SUCCESS_STATUS;
    },
    failure: (state) => {
      state.status = FAILURE_STATUS;
    },
    preparingAction: (state) => {
      state.status = PREPARING_ACTION_STATUS;
    },
    resetStatus: (state) => {
      state.status = IDLE_STATUS;
    },
    changeToTransferMode: (state) => {
      state.mode.main = TRANSFER_MODE;
    },
    changeToCreateMode: (state) => {
      state.mode.main = CREATE_MODE;
    },
    changeToConsultMode: (state) => {
      state.mode.main = CONSULT_MODE;
    },
    changeToUpdateMode: (state) => {
      state.mode.main = UPDATE_MODE;
    },
    resetMode: (state) => {
      state.mode.main = NONE_MODE;
    },
    changeToBackgroundCreateMode: (state) => {
      state.mode.background = BACKGROUND_CREATE_MODE;
    },
    resetBackgroundMode: (state) => {
      state.mode.background = NONE_BACKGROUND_MODE;
    },
    setModel: (state, action) => {
      state.model = {
        ...action.payload,
      };
    },
    resetModel: (state) => {
      state.model = initialState.model;
    },
    addToList: (state, action) => {
      state.list = [...state.list, action.payload.data];
    },
    resetList: (state) => {
      state.list = [];
    },
    updateOnList: (state, action) => {
      state.list = state.list.map((current) => {
        if (current.id === action.payload.data?.id) {
          return action.payload.data;
        }
        return current;
      });
    },
    setResponse: (state, action) => {
      state.response = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearResponse: (state) => {
      state.response = {
        status: null,
        message: null,
        data: null,
      };
    },
    setRelatedSelectionOperadoresList: (state, action) => {
      state.related.list.selectionOperadores = action.payload;
    },
    resetRelatedSelectionOperadoresList: (state, action) => {
      state.related.list.selectionOperadores = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchAllAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchAllAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaEmpresaEstrangeiraMensagem', {
            erro: action.error.message,
          }),
        };
      })
      .addCase(fetchFilteredAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchFilteredAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data.itens;
      })
      .addCase(fetchFilteredAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaEmpresaEstrangeiraMensagem', {
            erro: action.error.message,
          }),
        };
      })
      .addCase(fetchByIdAndInsertList.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchByIdAndInsertList.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        if (!action.payload.response.data) return;
        const empresa = state.list.find((el) => String(el.id) === String(action.payload.response.data.id));
        if (!empresa) {
          state.list = [...state.list, action.payload.response.data];
        }
      })
      .addCase(fetchByIdAndInsertList.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaEmpresaEstrangeiraMensagem', {
            erro: action.error.message,
          }),
        };
      })
      .addCase(fetchPaginateAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchPaginateAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data.itens;
        state.count = action?.payload?.data?.totalNumberOfItens;
      })
      .addCase(fetchPaginateAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaEmpresaEstrangeiraMensagem', {
            erro: action.error.message,
          }),
        };
      })
      .addCase(fetchByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(fetchByIdAsync.fulfilled, (state, action) => {
        if (action.payload.response.status === 200) {
          state.model = action.payload.response.data;
          state.status = SUCCESS_STATUS;
        }
      })
      .addCase(fetchByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarEmpresaEstrangeiraMensagem', {
            erro: action.error.message,
          }),
        };
      })
      .addCase(activateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(activateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { pessoaJuridica } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.empresaEstrangeiraAtivada', {
          empresa: pessoaJuridica.nome,
        });

        if (state.response.status === 200) {
          state.list = state.list.map((current) =>
            current.id === action.payload.response.data.id ? action.payload.response.data : current
          );
        }
      })
      .addCase(activateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroAtivarEmpresaEstrangeiraMensagem', { erro: action.error.message }),
        };
      })
      .addCase(inactivateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(inactivateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { pessoaJuridica } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.empresaEstrangeiraInativada', {
          empresa: pessoaJuridica.nome,
        });

        if (state.response.status === 200) {
          state.list = state.list.map((current) =>
            current.id === action.payload.response.data.id ? action.payload.response.data : current
          );
        }
      })
      .addCase(inactivateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroInativarEmpresaEstrangeiraMensagem', {
            erro: action.error.message,
          }),
        };
      });
  },
});

const {
  changeStatusTo,
  loading,
  success,
  failure,
  resetStatus,
  changeToBackgroundCreateMode,
  changeToConsultMode,
  changeToCreateMode,
  changeToUpdateMode,
  changeToTransferMode,
  preparingAction,
  resetBackgroundMode,
  resetList,
  resetMode,
  setModel,
  addToList,
  updateOnList,
  setResponse,
  clearResponse,
  setError,
  resetModel,
  setRelatedSelectionOperadoresList,
  resetRelatedSelectionOperadoresList,
} = empresaEstrangeiraSlice.actions;

const selectEmpresasEstrangeiras = (state: any) => state.empresaEstrangeira.list as ExportadorFabricante[];
const selectStatus = (state: any) => state.empresaEstrangeira.status;
const selectError = (state: any) => state.empresaEstrangeira.error;
const selectResponse = (state: any) => state.empresaEstrangeira.response;
const selectEmpresaEstrangeira = (state: any) => state.empresaEstrangeira.model as ExportadorFabricante;
const selectMode = (state: any) => state.empresaEstrangeira.mode.main;
const selectBackgroundMode = (state: any) => state.empresaEstrangeira.mode.background;
const selectEmpresaEstrangeiraCount = (state: any) => state.empresaEstrangeira.count;
const selectRelatedSelectionOperadoresList = (state: any) => state.empresaEstrangeira.related.list.selectionOperadores;

export {
  activateByIdAsync,
  addToList,
  changeStatusTo,
  changeToBackgroundCreateMode,
  changeToConsultMode,
  changeToCreateMode,
  changeToUpdateMode,
  changeToTransferMode,
  clearResponse,
  empresaEstrangeiraSlice,
  failure,
  fetchAllAsync,
  fetchByIdAndInsertList,
  fetchByIdAsync,
  fetchFilteredAsync,
  fetchPaginateAsync,
  inactivateByIdAsync,
  loading,
  preparingAction,
  resetBackgroundMode,
  resetList,
  resetMode,
  resetModel,
  resetStatus,
  selectBackgroundMode,
  selectEmpresaEstrangeira,
  selectEmpresaEstrangeiraCount,
  selectEmpresasEstrangeiras,
  selectError,
  selectMode,
  selectResponse,
  selectStatus,
  selectRelatedSelectionOperadoresList,
  setError,
  setModel,
  setResponse,
  success,
  updateOnList,
  setRelatedSelectionOperadoresList,
  resetRelatedSelectionOperadoresList,
};

export default empresaEstrangeiraSlice.reducer;
